<template>
  <div id="simulation" class="result container" :class="$mq">
    <h1 class="title">{{ $t("comparison.title") }}</h1>
    <p class="scenario container txt-left" v-if="economy < 0">{{ $t(`comparison.${scenario}`) }}</p>
    <div class="comparison container inline top space-between" v-if="$mq !== 'mobile'">
      <div class="compare">
        <Details
          :range="range"
          :rangeCompetitor="rangeCompetitor"
          :findDiscountDD="findDiscountDD"
          v-if="showDetails"
          @close="showDetails = false"
        ></Details>
        <!--  STAR ECONOMY  -->
        <div class="economy inline" v-if="economy > 0">
          <div class="amount">
            <span class="bold">{{ economy | toInteger }} €</span>
            {{ $t('comparison.product.economy') }}
          </div>
        </div>
        <!--  MEGA PRODUCT  -->
        <div class="product first">
          <div class="title inline">
            <img src="@/assets/logoMegaHeader.svg" />
          </div>
          <div class="price">
            <div class="inline center">
              <span class="bold">{{ range.amounts.annual | toCurrency }}</span>
              <span class="unit">€ {{ priceLabel }} /{{ $tc('Offer.Product.year') }}<sup v-if="!!findDiscountDD">**</sup></span>
            </div>
            <div class="inline center">
              <span>{{ range.amounts.monthly | toCurrency }}</span>
              <span class="unit">€ {{ priceLabel }} /{{ $tc('Offer.Product.month') }}</span>
            </div>
          </div>
          <button
            class="button secondary"
            @click="showDetails = true"
          >{{ $t('comparison.product.showDetail') }}</button>
          <div class="energy elec" v-if="hasElec && elecProducts">
            <div class="product-name space-between inline">
              <SvgIcon icon="elec" fill></SvgIcon>
              <select
                class="product-select"
                v-model="megaElecProduct"
                @change="selectProduct(megaElecProduct)" >
                <option
                  v-for="product in elecProducts.mega"
                  :key="product.name"
                  v-bind:value="product"
                >{{ product.descriptions[$i18n.locale] }}</option>
              </select>
              <SvgIcon :icon="`product-${range.name}`" fill :key="range.name"></SvgIcon>
              <span v-if="megaElecProduct.contractType === 'variable'"
                    class="question-mark"
                    @click="openModalHelp(megaElecProduct)"></span>
            </div>
            <table class="product-price">
              <tr>
                <td>{{ $t('comparison.product.subscription') }}</td>
                <td>{{ megaElecProduct.subscription[priceEuro] | toCurrency }} {{ $t('unity.annual') }}</td>
              </tr>
              <tr v-if="megaElecProduct.subtotal.discount">
                <td>
                    {{$t('comparison.product.discount')}}<span class="required">*</span>
                </td>
                <td>{{ megaElecProduct.subtotal.discount[priceEuro] | toCurrency }} {{ $t('unity.annual') }}</td>
              </tr>
              <tr v-for="line in cashbackLines(megaElecProduct)" :key="line.code">
                <td>
                    {{ $t('cashback.label') }}
                    <span class="mention">{{ cashbackMention(line.code) }}</span>
                </td>
                <td>{{ line.amount | toCurrency }} {{ $t('unity.annual') }}</td>
              </tr>
              <tr v-for="meterType in elecMeterTypes" :key="meterType">
                <td>{{ $tc(meterType, elecMeterTypes.length) }}</td>
                <td>{{ megaElecProduct.energy[meterType][unitaryPriceEuro] * 100 | toCurrency }} {{ $t('unity.centKWh') }}</td>
              </tr>
              <tr>
                <td>{{ $t('comparison.product.ecoTaxes') }}</td>
                <td>{{ megaElecProduct.subtotal.renewable[unitaryPriceEuro] * 100 | toCurrency }} {{ $t('unity.centKWh') }}</td>
              </tr>
            </table>
          </div>
          <div class="energy gas" v-if="hasGas && gasProducts">
            <div class="product-name space-between inline">
              <SvgIcon icon="gas" fill></SvgIcon>
              <select
                class="product-select"
                v-model="megaGasProduct"
                @change="selectProduct(megaGasProduct)"
              >
                <option
                  v-for="product in gasProducts.mega"
                  :key="product.name"
                  v-bind:value="product"
                >{{ product.descriptions[$i18n.locale] }}</option>
              </select>
              <SvgIcon :icon="`product-${range.name}`" fill :key="range.name"></SvgIcon>
              <span v-if="megaGasProduct.contractType === 'variable'"
                    class="question-mark"
                    @click="openModalHelp(megaGasProduct)"></span>
            </div>
            <table class="product-price">
              <tr>
                <td>{{ $t('comparison.product.subscription') }}</td>
                <td>{{ megaGasProduct.subscription[priceEuro] | toCurrency }} {{ $t('unity.annual') }}</td>
              </tr>
              <tr v-if="megaGasProduct.subtotal.discount">
                <td>
                    {{$t('comparison.product.discount')}}
                    <span class="required">
                        {{ megaGasProduct.range === 'COSY' ? '*' : megaGasProduct.range === 'SMART' ? '*' : ''}}
                    </span>
                </td>
                <td>{{ megaGasProduct.subtotal.discount[priceEuro] | toCurrency }} {{ $t('unity.annual') }}</td>
              </tr>
              <tr v-for="line in cashbackLines(megaGasProduct)" :key="line.code">
                <td>
                    {{ $t('cashback.label') }}
                    <span class="mention">{{ cashbackMention(line.code) }}</span>
                </td>
                <td>{{ line.amount | toCurrency }} {{ $t('unity.annual') }}</td>
              </tr>
              <tr>
                <td>{{ $t('comparison.product.consumption') }}</td>
                <td>{{ megaGasProduct.energy.day[unitaryPriceEuro] *100 | toCurrency }} {{ $t('unity.centKWh') }}</td>
              </tr>
            </table>
          </div>
          <div class="services">
            <p v-if="label('label1', 'mega')">{{ label('label1', 'mega') }}</p>
            <p v-if="label('label2', 'mega')">{{ label('label2', 'mega') }}</p>
            <p v-if="label('label3', 'mega')">{{ label('label3', 'mega') }}</p>
            <p v-if="label('label4', 'mega')">{{ label('label4', 'mega') }}</p>
            <p v-if="label('isElectricityGreen')">{{label('isElectricityGreen')}}</p>
          </div>
          <button v-if="!noSubscribe" @click="redirect()" class="button main">{{ $t('become') }}</button>
        </div>
      </div>
      <!-- COMPETITOR PRODUCT  -->
      <div class="compare">
        <div class="product second">
          <div class="title">
            <h2>{{ providerName }}</h2>
          </div>
          <div class="price">
            <div class="inline center">
              <span class="bold">{{ rangeCompetitor.amounts.annual | toCurrency }}</span>
              <span class="unit">€ {{ priceLabel }} /{{ $tc('Offer.Product.year') }}</span>
            </div>
            <div class="inline center">
              <span>{{ rangeCompetitor.amounts.monthly | toCurrency }}</span>
             <span class="unit">€ {{ priceLabel }} /{{ $tc('Offer.Product.month') }}</span>
            </div>
          </div>
          <button
            class="button secondary grey"
            @click="showDetails = true"
          >{{ $t('comparison.product.showDetail') }}</button>
          <div class="elec energy" v-if="hasElec && competitorElecProduct">
            <span v-if="competitorElecProduct.id == 'myElec'" class="htva">{{ $t('comparison.adviseHTVA') }}</span>
            <div class="product-name space-between inline">
              <SvgIcon icon="elec" fill></SvgIcon>
              <select
                class="product-select"
                v-model="competitorElecProduct"
                @change="selectProduct(competitorElecProduct)"
              >
                <option
                  v-for="product in elecProducts.competitor"
                  :key="product.name"
                  v-bind:value="product"
                >{{ product[productNameTrad] }}</option>
              </select>
            </div>
            <table class="product-price">
              <tr>
                <td>{{ $t('comparison.product.subscription') }}</td>
                <td
                  v-if="competitorElecProduct.id !== 'myElec'"
                >{{ competitorElecProduct.subscription.priceEuroVAT | toCurrency }} {{ $t('unity.annual') }}</td>
                <td v-else>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    class="myprice"
                    v-bind:value="competitorElecProduct.subscription.priceEuroVAT / tva | toInputCurrency"
                    @blur="competitorElecProduct.subscription.priceEuroVAT = validate($event.target.value)"
                  />
                  {{ $t('unity.annualHTVA') }}
                </td>
              </tr>
              <tr v-for="meterType in elecMeterTypes" :key="meterType">
                <td>{{ $tc(meterType, elecMeterTypes.length) }}</td>
                <td
                  v-if="competitorElecProduct.id !== 'myElec'"
                >{{ competitorElecProduct.energy[meterType].unitaryPriceEuroVAT * 100 | toCurrency }} {{ $t('unity.centKWh') }}</td>
                <td v-else>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    class="myprice"
                    v-bind:value="competitorElecProduct.energy[meterType].unitaryPriceEuroVAT * 100 / tva | toInputCurrency"
                    @blur="competitorElecProduct.energy[meterType].unitaryPriceEuroVAT = validateConsumption($event.target.value)"
                  />
                  {{ $t('unity.centKWhHTVA') }}
                </td>
              </tr>
              <tr>
                <td>{{ $t('comparison.product.ecoTaxes') }}</td>
                <td
                  v-if="competitorElecProduct.id !== 'myElec'"
                >{{ competitorElecProduct.subtotal.renewable.unitaryPriceEuroVAT * 100 | toCurrency }} {{ $t('unity.centKWh') }}</td>
                <td v-else>
                  <input
                    type="number"
                    step="0.01"
                    class="myprice"
                    v-bind:value="competitorElecProduct.subtotal.renewable.unitaryPriceEuroVAT * 100 / tva | toInputCurrency"
                    @blur="competitorElecProduct.subtotal.renewable.unitaryPriceEuroVAT = validateConsumption($event.target.value)"
                  />
                  {{ $t('unity.centKWhHTVA') }}
                </td>
              </tr>
            </table>
          </div>
          <div class="gas energy" v-if="hasGas && competitorGasProduct">
            <div class="product-name space-between inline">
              <SvgIcon icon="gas" fill></SvgIcon>
              <select
                class="product-select"
                v-model="competitorGasProduct"
                @change="selectProduct(competitorGasProduct)"
              >
                <option
                  v-for="product in gasProducts.competitor"
                  :key="product.name"
                  v-bind:value="product"
                >{{ product[productNameTrad] }}</option>
              </select>
            </div>
            <table class="product-price">
              <tr>
                <td>{{ $t('comparison.product.subscription') }}</td>
                <td
                  v-if="competitorGasProduct.id !== 'myGas'"
                >{{ competitorGasProduct.subscription.priceEuroVAT | toCurrency }} €/an</td>
                <td v-else>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    class="myprice"
                    v-bind:value="competitorGasProduct.subscription.priceEuroVAT / tva | toInputCurrency"
                    @blur="competitorGasProduct.subscription.priceEuroVAT = validate($event.target.value)"
                  />
                  {{ $t('unity.annualHTVA') }}
                </td>
              </tr>
              <tr>
                <td>{{ $t('comparison.product.consumption') }}</td>
                <td
                  v-if="competitorGasProduct.id !== 'myGas'"
                >{{ competitorGasProduct.energy.exclusiveDay.unitaryPriceEuroVAT * 100 | toCurrency }} {{ $t('unity.centKWh') }}</td>
                <td v-else>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    class="myprice"
                    v-bind:value="competitorGasProduct.energy.exclusiveDay.unitaryPriceEuroVAT * 100 / tva | toInputCurrency"
                    @blur="competitorGasProduct.energy.exclusiveDay.unitaryPriceEuroVAT = validateConsumption($event.target.value)"
                  />
                  {{ $t('unity.centKWhHTVA') }}
                </td>
              </tr>
            </table>
          </div>
          <div class="services grey">
            <p v-if="label('label1', 'competitor')">{{ label('label1', 'competitor') }}</p>
            <p v-if="label('label2', 'competitor')">{{ label('label2', 'competitor') }}</p>
            <p v-if="label('label3', 'competitor')">{{ label('label3', 'competitor') }}</p>
            <p v-if="label('label4', 'competitor')">{{ label('label4', 'competitor') }}</p>
          </div>
          <button @click="redirect()" class="button main grey" v-if="!noSubscribe">
            <span v-if="$i18n.locale === 'nl'">{{ providerName }}</span>
            {{ $t('comparison.product.quit') }}
            <span
              v-if="$i18n.locale === 'fr'"
            >{{ providerName }}</span>
          </button>
        </div>
      </div>
    </div>
    <ComparisonMobile
      v-if="$mq ==='mobile'"
      :range="range"
      :rangeCompetitor="rangeCompetitor"
      :findDiscountDD="findDiscountDD"
      @selectProduct="selectProduct"
      @showWarning="showVariable = true"
    ></ComparisonMobile>
    <WarningPrice v-if="showVariable" productCDI @close="showVariable = false"></WarningPrice>
    <div v-if="$mq ==='desktop' && !noSubscribe" class="inline center h-item-margin">
      <div class="mail inline space-around">
        <SendMail @send="sendMail()"></SendMail>
      </div>
      <div class="inline space-around" :class="$mq">
        <Print @generate="generatePdf()"></Print>
      </div>
    </div>
    <Informations v-if="$mq ==='desktop'"></Informations>
  </div>
</template>

<script>
import { mapState, mapGetters } from "vuex";
import ComparisonMobile from "./comparison/mobile/Comparison";
import Details from "./comparison/Details";
import Informations from "./components/Informations";
import SendMail from "./components/SendMail";
import Print from "./components/Print";
import WarningPrice from "./components/WarningPrice"
import leadService from "@/services/leadService";
import getDiscountDom from "@/mixins/getDiscountDom";
import productCDDorCDI from "@/mixins/productCDDorCDI";
import cashback from "@/mixins/cashback";


export default {
  name: "Comparison",
  mixins: [getDiscountDom, productCDDorCDI, cashback],
  data() {
    return {
      showDetails: false,
      loadingPrint: false,
      showVariable: false,
    };
  },
  computed: {
    ...mapState("common", ["uuid", "agentJwt", "agentRef"]),
    ...mapState("comparison", ["elecProducts", "gasProducts"]),
    ...mapGetters("comparison", ["amount", "total"]),
    ...mapGetters("consumption", ["elecMeterTypes"]),
    ...mapState("contact", ["isPro"]),
    ...mapState("calculation", [
      "providerElec",
      "providerGas",
      "providersElec",
      "providersGas"
    ]),
    ...mapState("energy", ["type"]),
    ...mapGetters("energy", ["hasElec", "hasGas"]),
    priceLabel() {
      return this.isPro ? this.$t('Offer.Product.ht') : this.$t('Offer.Product.ttc');
    },
    megaElecProduct: {
      get() {
        return this.$store.state.comparison.megaElecProduct;
      },
      set(value) {
        this.$store.commit("comparison/setMegaElecProduct", value);
      }
    },
    megaGasProduct: {
      get() {
        return this.$store.state.comparison.megaGasProduct;
      },
      set(value) {
        this.$store.commit("comparison/setMegaGasProduct", value);
      }
    },
    megaProduct() {
      return this.megaElecProduct || this.megaGasProduct;
    },
    competitorElecProduct: {
      get() {
        return this.$store.state.comparison.competitorElecProduct;
      },
      set(value) {
        this.$store.commit("comparison/setCompetitorElecProduct", value);
      }
    },
    competitorGasProduct: {
      get() {
        return this.$store.state.comparison.competitorGasProduct;
      },
      set(value) {
        this.$store.commit("comparison/setCompetitorGasProduct", value);
      }
    },
    competitorProduct() {
      return this.competitorElecProduct || this.competitorGasProduct;
    },
    productNameTrad() {
      return this.$i18n.locale === "fr" ? "labelFr" : "labelNl";
    },
    amountType() {
      return this.isPro ? "amountHTVA" : "amountTVAC";
    },
    priceEuro() {
      return this.isPro ? "priceEuro" : "priceEuroVAT";
    },
    unitaryPriceEuro() {
      return this.isPro ? "unitaryPriceEuro" : "unitaryPriceEuroVAT";
    },
    providerName() {
      let providerName = "";

      if (
        (this.providersElec && this.providersElec.length) ||
        (this.providersGas && this.providersGas.length)
      ) {
        switch (this.type) {
          case "elec":
            providerName = this.providersElec.find(
              provider => provider.id === this.providerElec
            ).name;
            break;
          case "gas":
            providerName = this.providersGas.find(
              provider => provider.id === this.providerGas
            ).name;
            break;
          case "both":
            if (this.providerElec === this.providerGas)
              providerName = this.providersElec.find(
                provider => provider.id === this.providerElec
              ).name;
            else providerName = this.providersElec.find(
              provider => provider.id === this.providerElec
            ).name + ' - ' + this.providersGas.find(
            provider => provider.id === this.providerGas
          ).name;

            break;
        }
      }

      return providerName;
    },
    range() {
      let range = {
        type: "mega",
        amounts: {
          annual: 0
        }
      };

      if (this.hasElec && this.megaElecProduct) {
        range["name"] = this.megaElecProduct.range;
        range["electricity"] = this.megaElecProduct;
        range.amounts["electricity"] = {
          energy: range.electricity.subtotal.energy[this.priceEuro],
          renewable: range.electricity.subtotal.renewable[this.priceEuro],
          taxes:
            (range.electricity.subtotal.taxes ? range.electricity.subtotal.taxes[this.priceEuro] : null) +
            (range.electricity.subtotal.taxesVAT ? range.electricity.subtotal.taxesVAT[this.priceEuro] : null),
          transport:
            range.electricity.subtotal.transportAndDistribution[this.priceEuro],
          prosumer:
            range.electricity.subtotal.prosumer !== undefined ? range.electricity.subtotal.prosumer[this.priceEuro] : 0,
          forfaitPv:
            range.electricity.subtotal.forfaitPv !== undefined ? range.electricity.subtotal.forfaitPv[this.priceEuro] : 0,
          total: range.electricity.total[this.priceEuro]
        };
      }

      if (this.hasGas && this.megaGasProduct) {
        range["name"] = this.megaGasProduct.range;
        range["gas"] = this.megaGasProduct;
        range.amounts["gas"] = {
          energy: range.gas.subtotal.energy[this.priceEuro],
          taxes:
                  (range.gas.subtotal.taxes ? range.gas.subtotal.taxes[this.priceEuro] : 0)+
                  (range.gas.subtotal.taxesVAT  ? range.gas.subtotal.taxesVAT[this.priceEuro] : 0),
          transport:
            range.gas.subtotal.transportAndDistribution[this.priceEuro],
          total: range.gas.total[this.priceEuro]
        };
      }

      if (this.hasElec && this.megaElecProduct)
        range.amounts.annual += range.amounts.electricity.total;
      if (this.hasGas && this.megaGasProduct)
        range.amounts.annual += range.amounts.gas.total;
      range.amounts["monthly"] = range.amounts.annual / 12;

      this.getDiscountDom(range);
      return range;
    },
    rangeCompetitor() {
      let range = {
        type: "competitor",
        amounts: {
          annual: 0
        }
      };

      if (this.hasElec && this.competitorElecProduct) {
        range["electricity"] = this.competitorElecProduct;
        let elecQuantity = 0;
        let elecEnergyAmount = 0;
        let elecRenewableAmount = 0;
        Object.keys(range.electricity.energy).forEach(key => {
          elecQuantity += range.electricity.energy[key].quantity;

          elecEnergyAmount +=
            parseFloat(range.electricity.energy[key].unitaryPriceEuroVAT) *
            range.electricity.energy[key].quantity;

          elecRenewableAmount +=
            parseFloat(
              range.electricity.subtotal.renewable.unitaryPriceEuroVAT
            ) * range.electricity.energy[key].quantity;
        });
        elecEnergyAmount += parseFloat(
          range.electricity.subscription.priceEuroVAT
        );
        let elecTaxes = range.electricity.subtotal.taxes.priceEuroVAT;
        let elecTransport =
          range.electricity.subtotal.transportAndDistribution.priceEuroVAT;
        let elecProsumer = range.electricity.subtotal.prosumer.priceEuroVAT;
        let elecForfaitPv = range.electricity.subtotal.forfaitPv !== undefined ? range.electricity.subtotal.forfaitPv.priceEuroVAT : null;

        range.amounts["electricity"] = {
          energy: elecEnergyAmount,
          quantity: elecQuantity,
          renewable: elecRenewableAmount,
          taxes: elecTaxes,
          transport: elecTransport,
          transport2: elecTransport,
          prosumer: elecProsumer,
          total:
            elecEnergyAmount + elecRenewableAmount + elecTaxes + elecTransport + elecProsumer + (elecForfaitPv? elecForfaitPv : 0)
        };

        if(elecForfaitPv !== null){
          range.amounts["electricity"].forfaitPv = elecForfaitPv;
        }
      }

      if (this.hasGas && this.competitorGasProduct) {
        range["gas"] = this.competitorGasProduct;

        let gasEnergyAmount =
          parseFloat(range.gas.energy.exclusiveDay.unitaryPriceEuroVAT) *
            range.gas.energy.exclusiveDay.quantity +
          parseFloat(range.gas.subscription.priceEuroVAT);
        let gasQuantity = range.gas.energy.exclusiveDay.quantity;

        let gasTaxes = range.gas.subtotal.taxes.priceEuroVAT;
        let gasTransport =
          range.gas.subtotal.transportAndDistribution.priceEuroVAT;

        range.amounts["gas"] = {
          energy: gasEnergyAmount,
          quantity: gasQuantity,
          taxes: gasTaxes,
          transport: gasTransport,
          total: gasEnergyAmount + gasTaxes + gasTransport
        };
      }

      if (this.hasElec && this.competitorElecProduct)
        range.amounts.annual += range.amounts.electricity.total;
      if (this.hasGas && this.competitorGasProduct)
        range.amounts.annual += range.amounts.gas.total;
      range.amounts["monthly"] = range.amounts.annual / 12;

      return range;
    },
    economy() {
      let economy =
        this.rangeCompetitor.amounts.annual - this.range.amounts.annual;

      let economyText = "";

      if (economy > 0) {
        economyText =
          this.$i18n.locale === "fr"
            ? ` et gagnez ${economy.toFixed(
                0
              )}€ par an ! Ça en fait des heures d'électricité ..`
            : ` in en win € ${economy.toFixed(
                0
              )} per jaar! Dat zijn heel wat uren elektriciteit..`;
      }
      this.$store.commit("comparison/setEconomyText", economyText);
      return economy;
    },
    scenario() {
      return (this.competitorElecProduct &&
        this.competitorElecProduct.id !== "myElec") ||
        (this.competitorGasProduct && this.competitorGasProduct.id !== "myGas")
        ? "scenario1"
        : "scenario2";
    },
    tva() {
      return this.isPro ? 1 : 1.06;
    },
    become() {
      return this.$store.getters["middleware/become"](this.range);
    },
    noSubscribe() {
      return this.$router.currentRoute.query.noSubscribe !== undefined || this.$store.state.common.noSubscribe;
    }
  },
  methods: {
    async redirect() {
      let data = {
        uuid: this.uuid,
      };

      if (this.range.electricity) {
        this.range.electricity['wantGreen'] = this.wantGreen ? 1 : 0;
        data["megaProductElec"] = this.range.electricity;
      }

      if (this.range.gas) {
        this.range.gas['wantGreen'] = this.wantGreen ? 1 : 0;
        data["megaProductGas"] = this.range.gas;
      }

      try {
        await leadService.save(data);
      } catch (error) {
        console.error(error.response);
      }

      if(this.agentJwt)
        window.location.href = `${process.env.BECOME}/agent/?newSession&agent=${this.agentRef}&jwt=${this.agentJwt}&lead=${this.uuid}&elec_custom_id=${this.$route.query['agent_ref_elec']}&gas_custom_id=${this.$route.query['agent_ref_gas']}`;
      else
        window.location.href = `${process.env.BECOME}/lead/${this.uuid}?newSession`;
    },
    sendMail() {
      this.$store.dispatch("comparison/sendMail");
    },
    async generatePdf() {
      this.loadingPrint = true;
      await this.$store.dispatch("comparison/generatePdf");
      this.loadingPrint = false;
    },
    validate(value) {
      this.$store.dispatch("comparison/save", "priceChanged");
      this.$store.commit(
        "comparison/setMyElecProduct",
        this.competitorElecProduct
      );
      this.$store.commit(
        "comparison/setMyGasProduct",
        this.competitorGasProduct
      );
      return !value || value < 0 ? 0 : parseFloat(value) * this.tva;
    },
    validateConsumption(value) {
      this.$store.dispatch("comparison/save", "priceChanged");
      this.$store.commit(
        "comparison/setMyElecProduct",
        this.competitorElecProduct
      );
      this.$store.commit(
        "comparison/setMyGasProduct",
        this.competitorGasProduct
      );
      return value < 0 ? 0 : (value = (value / 100) * this.tva);
    },
    productName(product) {
      return this.$store.getters["comparison/productName"](
        product
      ).toUpperCase();
    },
    selectProduct(product) {
      let peerProduct = this.$store.getters["comparison/findPeer"](product);

      if (peerProduct) {
        if (peerProduct.range !== undefined) {
          // Mega
          if (peerProduct.energyType === "electricity") {
            this.megaElecProduct = peerProduct;
          } else {
            this.megaGasProduct = peerProduct;
          }
        } else {
          // Competitor
          if (peerProduct.energyType === "electricity") {
            this.competitorElecProduct = peerProduct;
          } else {
            this.competitorGasProduct = peerProduct;
          }
        }
      }

      if (product.range === undefined) {
        this.$store.dispatch("comparison/setDefaultMegaProduct");
      }

      if (this.rangeCompetitor.electricity && this.range.electricity) {
        let savings =
          this.rangeCompetitor.amounts.electricity.total -
          this.range.amounts.electricity.total;
        this.$store.commit("comparison/setSavingsElec", savings);
      }

      if (this.rangeCompetitor.gas && this.range.gas) {
        let savings =
          this.rangeCompetitor.amounts.gas.total - this.range.amounts.gas.total;
        this.$store.commit("comparison/setSavingsGas", savings);
      }

      this.$store.dispatch("comparison/save", "productChanged");
    },
    label(labelName, range = 'mega') {
      let product = null;
      if(range === 'mega')
        product = this.range.electricity || this.range.gas;
      if(range === 'competitor')
        product = this.rangeCompetitor.electricity || this.rangeCompetitor.gas;
      let label = null;

      if (product.energyType === 'electricity' && labelName === 'isElectricityGreen'){
        return product.labels['isElectricityGreen'] ? this.$t(`greenElec`) : null;
      }

      if(product.labels === undefined)
        product['labels'] = this.$t(`Offer.Product.services.anyProduct.${product.energy}`);

       label = product.labels[labelName] ? product.labels[labelName][this.$i18n.locale] : "";

      return label.length ? label : null;
    },
  },
  watch: {
    competitorElecProduct: function(oldValue, newValue) {
      if (oldValue.id !== newValue.id) this.selectProduct(this.megaElecProduct);
    }
  },
  filters: {
    toCurrency: function(value) {
      return parseFloat(value ? value : 0)
        .toFixed(2)
        .replace(".", ",");
    },
    toInputCurrency: function(value) {
      return parseFloat(value ? value : 0).toFixed(2);
    },
    toInteger: function(value) {
      return parseFloat(value ? value : 0).toFixed(0);
    }
  },
  created() {
    if (this.rangeCompetitor.electricity && this.range.electricity) {
      let savings =
        this.rangeCompetitor.amounts.electricity.total -
        this.range.amounts.electricity.total;
      this.$store.commit("comparison/setSavingsElec", savings);
    }

    if (this.rangeCompetitor.gas && this.range.gas) {
      let savings =
        this.rangeCompetitor.amounts.gas.total - this.range.amounts.gas.total;
      this.$store.commit("comparison/setSavingsGas", savings);
    }

    this.$store.dispatch("comparison/save");

    this.getDiscountDom(this.range);

    window.dataLayer = window.dataLayer || [];
    dataLayer.push({
      event: "cookie-acceptation_true",
      "cookie-acceptation": "true"
    });
  },
  components: {
    ComparisonMobile,
    Details,
    Informations,
    SendMail,
    Print,
    WarningPrice
  }
};
</script>

<style lang="scss" scoped>
.title {
  margin: 0;
  margin-bottom: 20px!important;
  font-size: 1.7rem!important;
}


.scenario {
  padding: 0 15px;
  font-size: 0.8rem;
}

.comparison {
  margin: 0 auto;
  width: 700px!important;
}

.compare {
  width: 290px;
  // min-width: 300px;
  position: relative;

  .economy {
    position: absolute;
    width: 130px;
    height: 130px;
    z-index: 10;
    top: -10px;
    right: -60px;
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
    background: url("../assets/icons/icon-economy.svg") left;

    .amount {
      margin-top: -10px;
      width: 100px;
      font-size: 0.8rem;
      color: #000;

      .bold {
        font-size: 1.2rem;
        font-weight: 500;
      }
    }
    .icon {
      font-size: 10rem;
    }
  }

  &:first-child {
    margin-right: 80px;
  }

  .product {
    border-top: 15px solid $yellow;
    margin: 0 10px;
    background: $white;
    border-radius: 15px;
    box-shadow: 0px 1px 5px rgba(0, 0, 0, 0.3);

    .title {
      display: flex;
      flex-direction: column;
      justify-content: center;
      height: 70px;
      font-size: 1rem;
      font-weight: 700;

      img {
        height: 55%;
      }
    }

    .secondary {
      margin: 5px 0;
      width: 100%;
      display: block;
      text-decoration: underline $yellow;

      &.grey {
        text-decoration: underline #e4e4e4;
      }
    }

    .services {
      font-size: 0.8rem;
      background: #FEF1C4;
      margin-bottom: 10px;
      padding: 5px;

      p {
        text-align: left;
        margin: 10px;
      }

      &.grey {
        background: #e4e4e4;
      }
    }

    .details {
      padding: 5px 10px;
      margin: 10px;
      border: 0;
      background: none;
      color: #fdcb47;
      border: 2px solid #fdcb47;

      &.grey {
        color: #e4e4e4;
        border: 2px solid #e4e4e4;
      }
    }

    .price {
      padding: 10px 0;

      div {
        margin-bottom: 5px;
      }

      .unit {
        display: block;
        font-size: 0.5rem;
        width: 45px;
      }

      .bold {
        font-weight: bold;
      }
    }

    .energy {
      color: #231f20;
      margin-bottom: 20px;

      .product-name {
        font-size: 1.5rem;
        margin: 0 5%;
        border: 1px solid #f7f7f7;

        select {
          width: 100%;
          padding: 5px;
          border: 0;
          margin-left: 0px;
          flex: 1;
        }
      }

      .product-price {
        margin-top: 20px;
        text-align: left;
        font-size: 0.8rem;
        width: 100%;
        border: 0;
        border-collapse: collapse;

        tr {
          &:nth-child(odd) {
            background: #f7f7f7;
          }

          td {
            padding: 7px 10px;

            .myprice {
              text-align: right;
              border: 1px solid #eee;
              width: 60px;
            }

            .mention {
              display: block;
              font-size: 0.7rem;
              font-style: italic;
            }
          }
        }
      }
    }

    .detail {
      font-size: 0.8rem;
      padding: 20px;
    }

    .button {
      margin-bottom: 10px;
      &.main {
        background: $yellow !important;
      }

      &.grey {
        background: $white !important;
      }
    }

    &.first {
      z-index: 2;
      box-shadow: rgba(77, 4, 4, 0.158) 5px 10px 10px;

      .price {
        background: #fdcb47;
      }
    }

    &.second {
      border-top: 15px solid #e4e4e4;

      .title {
        h2 {
          font-weight: bold!important;
          text-align: center!important;
        }

        .icon {
          color: #e4e4e4;
        }
      }

      .price {
        color: $black;
        background: #e4e4e4;
      }
    }
    margin-bottom: 20px;
  }
}

.mail {
  padding: 10px 0;
}

.htva {
  display: block;
  font-size: .8rem;
  margin-bottom: 5px;
}

.warning {
  cursor: pointer;
  display: inline-block;
  height: 13px;
  width: 13px;
  background:url("~@/assets/icons/icon-warning.svg") no-repeat bottom/contain;
}

.disclaimer {
  margin: 1rem;
  font-size: 0.7rem;
}
</style>



// WEBPACK FOOTER //
// src/views/Comparison.vue